// src/components/block/liveStreamManagement/XtreamStreamSelector.tsx
//
// Sélecteur de flux live Xtream Codes.
// Inspiré visuellement de MediaSelector : grille de vignettes avec icône + nom.
// Sélection unique → bouton Valider.

import { WWW_URLS } from '../../../backendServices/urls';
import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, Search, Tv, Check } from 'lucide-react';

import { XtreamLiveStream } from '../../../dataTypes/liveStreamManagment/types_xtreamCode';

// ── Props ─────────────────────────────────────────────────────────────────────

interface XtreamStreamSelectorProps {
  streams   : XtreamLiveStream[];
  onSelect  : (stream: XtreamLiveStream) => void;
  onClose   : () => void;
}

// ── Constantes ────────────────────────────────────────────────────────────────

const CARD_W = 160;
const CARD_H = 130;

// ── Composant ─────────────────────────────────────────────────────────────────

const XtreamStreamSelector: React.FC<XtreamStreamSelectorProps> = ({
  streams,
  onSelect,
  onClose,
}) => {
  const [filter,   setFilter]   = useState('');
  const [selected, setSelected] = useState<XtreamLiveStream | null>(null);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return streams;
    return streams.filter(s => s.name.toLowerCase().includes(q));
  }, [streams, filter]);

  const handleConfirm = () => {
    if (selected) onSelect(selected);
  };

  return ReactDOM.createPortal(
    <div
      style={{
        position      : 'fixed',
        inset         : 0,
        zIndex        : 10000,
        background    : 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(5px)',
        display       : 'flex',
        alignItems    : 'center',
        justifyContent: 'center',
        padding       : 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background  : 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
          border      : '1px solid #334155',
          borderRadius: 16,
          width       : '90vw',
          maxWidth    : 1100,
          maxHeight   : '90vh',
          boxShadow   : '0 24px 64px rgba(0,0,0,0.6)',
          display     : 'flex',
          flexDirection: 'column',
          overflow    : 'hidden',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          display      : 'flex',
          alignItems   : 'center',
          gap          : 10,
          padding      : '14px 20px',
          borderBottom : '1px solid #334155',
          flexShrink   : 0,
        }}>
          <Tv style={{ width: 18, height: 18, color: '#f472b6' }} />
          <span style={{ fontSize: 15, fontWeight: 700, color: '#fff', flex: 1 }}>
            Sélectionner un flux Xtream
          </span>
          <span style={{
            fontSize  : 11,
            color     : '#64748b',
            fontFamily: 'monospace',
          }}>
            {filtered.length} / {streams.length} flux
          </span>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 4, borderRadius: 6 }}
          >
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* ── Barre de filtre ── */}
        <div style={{
          padding     : '12px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          flexShrink  : 0,
        }}>
          <div style={{ position: 'relative' }}>
            <Search style={{
              position : 'absolute',
              left     : 10,
              top      : '50%',
              transform: 'translateY(-50%)',
              width    : 14,
              height   : 14,
              color    : '#64748b',
              pointerEvents: 'none',
            }} />
            <input
              type="text"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Filtrer par nom…"
              autoFocus
              style={{
                width          : '100%',
                padding        : '8px 12px 8px 32px',
                borderRadius   : 8,
                background     : '#0f172a',
                border         : '1px solid #334155',
                color          : '#e2e8f0',
                fontSize       : 13,
                outline        : 'none',
                boxSizing      : 'border-box' as React.CSSProperties['boxSizing'],
              }}
            />
          </div>
        </div>

        {/* ── Grille ── */}
        <div style={{
          flex      : 1,
          overflowY : 'auto',
          padding   : '16px 20px',
        }}>
          {filtered.length === 0 ? (
            <div style={{
              display        : 'flex',
              flexDirection  : 'column',
              alignItems     : 'center',
              justifyContent : 'center',
              gap            : 12,
              height         : 200,
              color          : '#475569',
            }}>
              <Tv style={{ width: 32, height: 32 }} />
              <span style={{ fontSize: 13 }}>Aucun flux trouvé</span>
            </div>
          ) : (
            <div style={{
              display              : 'grid',
              gridTemplateColumns  : `repeat(auto-fill, minmax(${CARD_W}px, 1fr))`,
              gap                  : 10,
            }}>
              {filtered.map(stream => {
                const isSelected = selected?.stream_id === stream.stream_id;
                return (
                  <StreamCard
                    key={stream.stream_id}
                    stream={stream}
                    isSelected={isSelected}
                    onClick={() => setSelected(stream)}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding      : '10px 20px',
          borderTop    : '1px solid rgba(255,255,255,0.08)',
          display      : 'flex',
          alignItems   : 'center',
          justifyContent: 'space-between',
          flexShrink   : 0,
          gap          : 10,
        }}>
          <div style={{ fontSize: 12, color: '#475569', fontStyle: 'italic' }}>
            {selected
              ? <span style={{ color: '#a5b4fc' }}>✓ &nbsp;{selected.name}</span>
              : 'Aucun flux sélectionné'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.06)',
                border    : '1px solid rgba(255,255,255,0.15)',
                borderRadius: 6,
                color     : '#94a3b8',
                fontSize  : 12,
                fontWeight: 700,
                padding   : '6px 20px',
                cursor    : 'pointer',
              }}
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selected}
              style={{
                background  : selected ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.06)',
                border      : `1px solid ${selected ? 'rgba(99,102,241,0.6)' : 'rgba(99,102,241,0.2)'}`,
                borderRadius: 6,
                color       : selected ? '#a5b4fc' : '#475569',
                fontSize    : 12,
                fontWeight  : 700,
                padding     : '6px 20px',
                cursor      : selected ? 'pointer' : 'not-allowed',
                transition  : 'all 0.15s',
              }}
            >
              Valider
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── StreamCard ────────────────────────────────────────────────────────────────

const StreamCard: React.FC<{
  stream    : XtreamLiveStream;
  isSelected: boolean;
  onClick   : () => void;
}> = ({ stream, isSelected, onClick }) => {
  const [imgError, setImgError] = useState(false);

  return (
    <button
      onClick={onClick}
      title={stream.name}
      style={{
        display      : 'flex',
        flexDirection: 'column',
        alignItems   : 'center',
        gap          : 8,
        padding      : '12px 8px',
        height       : CARD_H,
        borderRadius : 10,
        background   : isSelected
          ? 'linear-gradient(135deg, rgba(99,102,241,0.25), rgba(139,92,246,0.15))'
          : 'rgba(255,255,255,0.03)',
        border       : `2px solid ${isSelected ? '#818cf8' : 'rgba(255,255,255,0.07)'}`,
        cursor       : 'pointer',
        transition   : 'all 0.15s',
        position     : 'relative',
        boxShadow    : isSelected ? '0 0 16px rgba(99,102,241,0.25)' : 'none',
      }}
      onMouseEnter={e => {
        if (!isSelected) {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.07)';
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.18)';
        }
      }}
      onMouseLeave={e => {
        if (!isSelected) {
          (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.03)';
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.07)';
        }
      }}
    >
      {/* Pastille de sélection */}
      {isSelected && (
        <div style={{
          position    : 'absolute',
          top         : 6,
          right       : 6,
          width       : 18,
          height      : 18,
          borderRadius: '50%',
          background  : '#818cf8',
          display     : 'flex',
          alignItems  : 'center',
          justifyContent: 'center',
        }}>
          <Check style={{ width: 10, height: 10, color: '#fff' }} />
        </div>
      )}

      {/* Icône / fallback */}
      <div style={{
        width         : 56,
        height        : 56,
        borderRadius  : 8,
        overflow      : 'hidden',
        display       : 'flex',
        alignItems    : 'center',
        justifyContent: 'center',
        background    : 'rgba(255,255,255,0.05)',
        flexShrink    : 0,
      }}>
        {stream.stream_icon && !imgError ? (
          <img
            src={
  stream.stream_icon.startsWith('http://')
    ? `${WWW_URLS.broadcastingServer}/http-proxy?url=${stream.stream_icon}`
    : stream.stream_icon
}
            alt={stream.name}
            onError={() => setImgError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <Tv style={{ width: 24, height: 24, color: '#475569' }} />
        )}
      </div>

      {/* Nom */}
      <span style={{
        fontSize    : 11,
        fontWeight  : 600,
        color       : isSelected ? '#c7d2fe' : '#94a3b8',
        textAlign   : 'center',
        lineHeight  : 1.3,
        overflow    : 'hidden',
        display     : '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical' as React.CSSProperties['WebkitBoxOrient'],
        wordBreak   : 'break-word',
        width       : '100%',
      }}>
        {stream.name}
      </span>
    </button>
  );
};

export default XtreamStreamSelector;